import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

function downloadDetailsFile(path, filename) {
  const link = document.createElement('a');
  link.href = path;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

async function downloadCategoryReport(selectedCodes, headers, filename) {
  const query = selectedCodes.map(c => `category=${encodeURIComponent(c)}`).join('&');
  const res = await axios.get(`/api/mfd/report/top-category?${query}`, {
    headers,
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${filename}.xlsx`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export default function MFDCategoryReport() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };

  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ text: '', error: false });

  useEffect(() => {
    axios.get('/api/mfd/categories', { headers })
      .then(res => setCategories(res.data || []))
      .catch(() => {});
  }, [token]);

  const showMsg = (text, error = false) => {
    setMsg({ text, error });
    setTimeout(() => setMsg({ text: '', error: false }), 5000);
  };

  const toggle = (code) => {
    setSelected(prev => {
      if (prev.includes(code)) return prev.filter(c => c !== code);
      if (prev.length >= 10) return prev;
      return [...prev, code];
    });
  };

  const handleGenerate = async () => {
    setLoading(true);
    try {
      await downloadCategoryReport(selected, headers, 'Category_Report');
      showMsg('Category Report downloaded');
    } catch (e) {
      showMsg(
        `Download failed: ${e.response?.data?.error || e.response?.data?.message || e.message}`,
        true
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.navbar}>
        <div style={s.navLeft}>
          <button onClick={() => navigate('/mfd')} style={s.backBtn}>&#8592; Dashboard</button>
          <span style={s.brand}>Category Report</span>
        </div>
        <span style={s.reportBadge}>R01</span>
      </div>

      <div style={s.container}>
        <p style={s.pageDesc}>
          Select up to 10 categories. Generates an Excel with the top 4 Regular Plan funds
          per category, ranked by FIP Score.
        </p>

        {msg.text && (
          <div style={{ ...s.msgBox, ...(msg.error ? s.msgError : s.msgSuccess) }}>
            {msg.error ? 'Warning: ' : 'OK: '}{msg.text}
          </div>
        )}

        <div style={s.pickerCard}>
          <div style={s.counterRow}>
            <span style={s.counterBadge}>{selected.length} / 10 selected</span>
            {selected.length > 0 && (
              <span style={s.clearLink} onClick={() => setSelected([])}>Clear all</span>
            )}
          </div>

          {selected.length >= 10 && (
            <div style={s.maxNote}>
              Maximum 10 categories selected. Deselect one to choose another.
            </div>
          )}

          {categories.map(({ family, categories: cats }) => (
            <div key={family} style={s.familyBlock}>
              <div style={s.familyHeader}>{family}</div>
              <div style={s.tagCloud}>
                {cats.map(cat => {
                  const isSelected = selected.includes(cat.code);
                  const isDisabled = !isSelected && selected.length >= 10;
                  return (
                    <span
                      key={cat.code}
                      style={isSelected ? s.tagSelected : (isDisabled ? s.tagDisabled : s.tag)}
                      onClick={() => !isDisabled && toggle(cat.code)}
                    >
                      {cat.label}
                    </span>
                  );
                })}
              </div>
            </div>
          ))}

          <div style={s.btnRow}>
            <button
              style={selected.length === 0 || loading ? s.genBtnOff : s.genBtn}
              disabled={selected.length === 0 || loading}
              onClick={handleGenerate}
            >
              {loading ? 'Generating...' : 'Generate Report'}
            </button>
            <button style={s.detailsBtn} onClick={() => downloadDetailsFile('/details/R01_Category_Report_Details.pdf', 'R01_Category_Report_Details.pdf')}>
              Details
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const s = {
  page:        { minHeight: '100vh', background: '#f5f7fa', fontFamily: "'Segoe UI', sans-serif" },
  navbar:      { background: '#1565C0', color: '#fff', padding: '14px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  navLeft:     { display: 'flex', alignItems: 'center', gap: '16px' },
  backBtn:     { background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '6px', padding: '7px 14px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' },
  brand:       { fontWeight: '700', fontSize: '17px' },
  reportBadge: { fontSize: '11px', fontWeight: '700', color: '#1565C0', background: '#E3F0FF', padding: '3px 10px', borderRadius: '20px' },
  container:   { maxWidth: '860px', margin: '0 auto', padding: '32px 24px' },
  pageDesc:    { fontSize: '14px', color: '#555', marginBottom: '20px', lineHeight: '1.6' },
  msgBox:      { borderRadius: '8px', padding: '12px 16px', marginBottom: '20px', fontSize: '14px' },
  msgSuccess:  { background: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7' },
  msgError:    { background: '#ffebee', color: '#CC0000', border: '1px solid #ffcdd2' },
  pickerCard:  { background: '#fff', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' },
  counterRow:  { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' },
  counterBadge:{ background: '#1565C0', color: '#fff', fontSize: '12px', fontWeight: '700', padding: '4px 12px', borderRadius: '20px' },
  clearLink:   { fontSize: '12px', color: '#CC0000', cursor: 'pointer' },
  maxNote:     { fontSize: '12px', color: '#CC0000', background: '#ffebee', border: '1px solid #ffcdd2', borderRadius: '6px', padding: '7px 12px', marginBottom: '12px' },
  familyBlock: { marginBottom: '16px' },
  familyHeader:{ fontSize: '11px', fontWeight: '700', color: '#444', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '7px' },
  tagCloud:    { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  tag:         { fontSize: '12px', padding: '5px 11px', borderRadius: '20px', border: '1px solid #ddd', color: '#555', background: '#fff', cursor: 'pointer', userSelect: 'none' },
  tagSelected: { fontSize: '12px', padding: '5px 11px', borderRadius: '20px', border: '1px solid #1565C0', color: '#1565C0', background: '#E3F0FF', cursor: 'pointer', userSelect: 'none', fontWeight: '600' },
  tagDisabled: { fontSize: '12px', padding: '5px 11px', borderRadius: '20px', border: '1px solid #eee', color: '#bbb', background: '#f5f5f5', cursor: 'not-allowed', userSelect: 'none' },
  btnRow:      { marginTop: '24px' },
  genBtn:      { background: '#2e7d32', color: '#fff', border: 'none', borderRadius: '8px', padding: '11px 28px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  genBtnOff:   { background: '#bdbdbd', color: '#fff', border: 'none', borderRadius: '8px', padding: '11px 28px', fontSize: '14px', fontWeight: '700', cursor: 'not-allowed' },
  detailsBtn:  { background: '#616161', color: '#fff', border: 'none', borderRadius: '8px', padding: '11px 28px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
};
